import { Divider, Box, Stack } from '@mui/material';
import type {
  FileSnapshot,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { identifyDelimiter } from '@/app/(dashboard)/search/searchUtils';
import { MonthSelector } from '@/components/date/monthSelector';
import { Loader } from '@/components/layout/loading';
import { SnapshotsList } from '@/components/panels/instance/tabs/eon-snapshots/snapshotsList';
import { SnapshotSelectionEmptyState } from '@/components/snapshots/snapshotSelection/snapshotSelectionEmptyState';
import { SnapshotSelectionHeader } from '@/components/snapshots/snapshotSelection/snapshotSelectionHeader';
import { VaultsTabs } from '@/components/vaults/vaultTabs';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { isDateValid } from '@/utils/dateTime';
import { dayjs } from '@/utils/dayjs';
import { isEmptyObject } from '@/utils/emptyUtil';

import { StepContainer } from '../../wizard/StepContainer';

interface SelectSnapshotProps {
  fileSnapshot?: FileSnapshot;
  vaultId?: string;
  resourceId?: string;
  resource: InventoryResource;
  files: { path: string; isDirectory: boolean }[];
}

type SelectSnapshotStepProps<T> = StepProps<T & SelectSnapshotProps>;

export const SelectSnapshotStep = <T,>(props: SelectSnapshotStepProps<T>) => {
  const { wizardState, setWizardState } = props;
  const { resource } = wizardState;
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();
  const [selectedVaultId, setSelectedVaultId] = useState<string>();
  const [selectedSnapshot, setSelectedSnapshot] = useState<
    FileSnapshot | undefined
  >();
  const dal = useDAL();

  const filePath = wizardState.files[0]?.path ?? '';

  const {
    data: inventorySnapshotsBody,
    isLoading: isLoadingInventorySnapshots,
  } = dal.inventory.snapshots.useList(resource.id, 0, 100);

  const snapsByVault = useMemo(() => {
    const inventorySnapshots =
      inventorySnapshotsBody?.data.filter((x) => isDateValid(x.pointInTime)) ||
      [];
    return groupByMonthYearAndVaultId(inventorySnapshots);
  }, [inventorySnapshotsBody]);

  const months = useMemo(
    () => Object.keys(snapsByVault).sort((a, b) => b.localeCompare(a)),
    [snapsByVault]
  );

  const vaultIds = useMemo(
    () =>
      selectedMonth &&
      snapsByVault[selectedMonth] &&
      Object.keys(snapsByVault[selectedMonth]),
    [selectedMonth, snapsByVault]
  );

  useEffect(() => {
    setSelectedMonth(months[0]);
  }, [months]);

  useEffect(() => {
    if (vaultIds) {
      setSelectedVaultId(vaultIds[0]);
    }
  }, [vaultIds]);

  const handleSnapshotChange = (snapshot: FileSnapshot | undefined) => {
    setSelectedSnapshot(snapshot);
    if (snapshot && selectedVaultId) {
      setWizardState((prev) => ({
        ...prev,
        fileSnapshot: snapshot,
        vaultId: selectedVaultId,
      }));
    }
  };

  const delimiter = identifyDelimiter(filePath);
  const fileName = filePath.split(delimiter).pop() || filePath;

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={0}
      canGoNext={() => !!selectedSnapshot && !!selectedVaultId}
      onNextClick={async () => props.setNextStep(props.currentStep.next?.[0])}
      sx={{ padding: 0, overflow: 'hidden' }}
    >
      {isLoadingInventorySnapshots ? (
        <Box marginTop='100px'>
          <Loader />
        </Box>
      ) : !selectedVaultId || isEmptyObject(snapsByVault) || !selectedMonth ? (
        <SnapshotSelectionEmptyState description='No Eon snapshots available' />
      ) : (
        <Stack height='100%' direction='column'>
          <SnapshotSelectionHeader fileName={fileName} />
          <Divider />
          <MonthSelector months={months} onMonthChange={setSelectedMonth} />
          <Divider />
          <VaultsTabs
            onVaultChange={setSelectedVaultId}
            vaults={vaultIds || []}
          />
          <Divider />
          <SnapshotsList
            inventorySnapshots={
              snapsByVault[selectedMonth][selectedVaultId] || []
            }
            selectedMonth={dayjs.utc(selectedMonth).toDate()}
            onRowSelectionChange={handleSnapshotChange}
            vaultId={selectedVaultId}
            resourceId={resource.id}
            filePath={filePath}
            selectedSnapshot={selectedSnapshot}
          />
        </Stack>
      )}
    </StepContainer>
  );
};

function groupByMonthYearAndVaultId(inventorySnapshots: Snapshot[]) {
  const groupByDate = Object.groupBy(inventorySnapshots, (snap) =>
    dayjs.utc(snap.pointInTime).format('YYYY-MM')
  );

  const snapsByDateByVault: Record<
    string,
    Record<string, Snapshot[] | undefined>
  > = {};
  Object.keys(groupByDate).forEach((key) => {
    const snapsByDate: Snapshot[] = groupByDate[key]!.filter(
      (snap) => snap.vaultId
    );
    const groupByVault: Record<string, Snapshot[] | undefined> =
      Object.groupBy(snapsByDate, (snap) => snap.vaultId!) || {};
    snapsByDateByVault[key] = groupByVault || [];
  });

  return snapsByDateByVault;
}
